import { useState, useEffect, useRef, useMemo } from 'react';
import { Mail, MapPin, Linkedin, Twitter, Instagram, Youtube, Facebook, Globe, ChevronDown } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FooterContent, FooterSocialLink } from '../../types';
import api from '../../services/api';
import { resolveLocalized } from '../../utils/localization';

const SOCIAL_ICONS: Record<FooterSocialLink['platform'], React.ComponentType<{ className?: string }>> = {
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube,
  facebook: Facebook,
};

const languages = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'zh-CN', label: '简体', name: '简体中文' },
  { code: 'zh-HK', label: '繁體', name: '繁體中文' },
];

export default function Footer() {
  const [cmsContent, setCmsContent] = useState<FooterContent | null>(null);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [siteName, setSiteName] = useState('IABFM');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const defaultFooter: FooterContent = useMemo(() => ({
    tagline: t('footer.tagline'),
    columns: [
      {
        title: t('footer.programs'),
        links: [
          { label: t('footer.cfaPreparation'), type: 'link', target: '/courses' },
          { label: t('footer.businessStrategy'), type: 'link', target: '/courses?category=Leadership' },
          { label: t('footer.wealthManagement'), type: 'link', target: '/courses?category=Wealth+Management' },
          { label: t('footer.fintech'), type: 'link', target: '/courses?category=FinTech' },
        ],
      },
      {
        title: t('footer.resources'),
        links: [
          { label: t('footer.courseCurriculum'), type: 'scroll', target: 'outline' },
          { label: t('footer.checkCertification'), type: 'link', target: '/verify-certificate' },
          { label: t('footer.successStories'), type: 'scroll', target: 'testimonials' },
        ],
      },
    ],
    contactEmail: 'info@iabfmchina.org',
    contactAddress: '811, 8 floor, Tower 3, China Hong Kong City, TST, Hong Kong SAR',
    copyrightText: t('footer.copyright'),
    socialLinks: [
      { platform: 'linkedin', url: '#', visible: true },
      { platform: 'twitter', url: '#', visible: true },
      { platform: 'instagram', url: '#', visible: true },
      { platform: 'youtube', url: '#', visible: true },
    ],
  }), [t]);

  const content = cmsContent || defaultFooter;

  useEffect(() => {
    api.get('/site-content').then((res) => {
      if (res.data?.footer) {
        setCmsContent(res.data.footer);
      }
      if (res.data?.branding) {
        if (res.data.branding.logoUrl) setLogoUrl(res.data.branding.logoUrl);
        if (res.data.branding.siteName) setSiteName(res.data.branding.siteName);
      }
    }).catch(() => {
      // Silently fall back to defaults
    });
  }, []);

  // Close language dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleScrollLink = (target: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: target } });
    } else {
      const element = document.getElementById(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const visibleSocials = content.socialLinks.filter(s => s.visible);
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <footer className="py-12 bg-[#2C2418]">
      {/* Gradient line top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={logoUrl} alt={siteName} className="h-24 -my-7" />
              <span className="font-display text-2xl font-bold text-white">{siteName}</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {resolveLocalized(content.tagline as any, i18n.language)}
            </p>
          </div>

          {/* Dynamic Columns */}
          {content.columns.map((col, colIdx) => (
            <div key={colIdx}>
              <h4 className="font-semibold mb-4 text-gold uppercase tracking-wider text-sm">{resolveLocalized(col.title as any, i18n.language)}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {col.links.map((link, linkIdx) => (
                  <li key={linkIdx}>
                    {link.type === 'link' ? (
                      <Link to={link.target} className="hover:text-gold transition-colors duration-200">
                        {resolveLocalized(link.label as any, i18n.language)}
                      </Link>
                    ) : (
                      <button
                        onClick={() => handleScrollLink(link.target)}
                        className="hover:text-gold transition-colors duration-200"
                      >
                        {resolveLocalized(link.label as any, i18n.language)}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column */}
          <div>
            <h4 className="font-semibold mb-4 text-gold uppercase tracking-wider text-sm">{t('footer.contact')}</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {content.contactEmail}
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{resolveLocalized(content.contactAddress as any, i18n.language)}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            &copy; {resolveLocalized(content.copyrightText as any, i18n.language)}
          </p>

          {/* Language Dropdown */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-gray-400 hover:text-gold border border-white/10 hover:border-gold/30 transition-all duration-200"
            >
              <Globe className="w-4 h-4" />
              <span>{currentLang.name}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#3a3226] rounded-xl shadow-xl border border-white/10 overflow-hidden min-w-[140px] py-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                      i18n.language === lang.code
                        ? 'text-gold bg-gold/10'
                        : 'text-gray-400 hover:text-gold hover:bg-white/5'
                    }`}
                  >
                    <span>{lang.name}</span>
                    <span className="text-xs opacity-60">{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {visibleSocials.map((social, idx) => {
              const Icon = SOCIAL_ICONS[social.platform];
              return (
                <a
                  key={idx}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-gold/10 hover:text-gold transition-all duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
